import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Image,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import type { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import {
  BadgePercent,
  Building2,
  Camera,
  ChevronLeft,
  ChevronRight,
  Check,
  Database,
  ExternalLink,
  FileText,
  HeartHandshake,
  Layers,
  Mail,
  Phone,
  Pill,
  Scale,
  Syringe,
  Ticket,
  DollarSign,
  Shield,
  Sparkles,
  Store,
  Truck,
  RefreshCw,
  UploadCloud,
} from "lucide-react-native";
import { SvgUri } from "react-native-svg";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Asset } from "expo-asset";
import { getTodayString } from "@src/packages/utils/date";
import {
  readFileForUpload,
  resolveUploadContentType,
} from "@src/packages/utils/fileUpload";
import {
  useCompleteUpload,
  useCreateDocument,
  useDocuments,
  useGetDownloadUrl,
  useGetUploadUrl,
  useUploadFile,
  type Document as PatientDocument,
} from "@src/packages/documents/hooks/useDocuments";
import { getMedicationByBrandName } from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import type { HomepageMedicationFactSheetPayload } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import {
  MOCK_PHARMACY_MEDICATIONS,
  MOCK_PHARMACY_QUOTES,
  PHARMACY_SAVINGS_PROGRAMS,
  usePharmacyDirectory,
  type PharmacyDemoQuote,
  type PharmacySavingsProgram,
  type PharmacyDirectoryRecord,
  type PharmacyDirectoryType,
} from "@src/packages/providers";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList, MainTabParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import {
  MoonFilterChip,
  MoonSearchInput,
  MoonToggle,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogPrimaryButton,
  DialogSecondaryButton,
  Row,
  SectionTitle,
  Tag,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import MedicationFactsheetSheet from "../../components/MedicationFactsheetSheet";
import type { MedicationUserContext } from "../../components/MedicationFactsheetSheet";
import CostsCardTabs from "../../components/CostsCardTabs";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import PharmacyHeader from "../../components/access/PharmacyHeader";
import {
  pharmacyFulfillmentCards,
  PharmacyActionCard,
  PharmacyFulfillmentCard as DesignedPharmacyFulfillmentCard,
} from "../care/suggested/CareReviewConceptScreen";

type Props = NativeStackScreenProps<AccessStackParamList, "Pharmacy">;
type MedicationForm = "pen" | "vial" | "syringe" | "tablet" | "suppository";
type DisplayMedication = {
  id: string;
  name: string;
  dosage: string;
  form: MedicationForm;
  route?: string;
  coverage?: "covered" | "cash_pay";
};

type MedicationStorageProfile = {
  statusKey: string;
  statusTone: "blue" | "green" | "neutral";
  windowKey: string;
  detailKey: string;
};

type PharmacyTab = "prescription" | "quotes" | "savings";

type PharmacyPatientQuote = {
  id: string;
  pharmacyName: string;
  totalPrice: number | null;
  quoteDate: string;
  note: string;
  source: "manual" | "uploaded";
  documentName?: string;
  uploadId?: string | null;
  documentId?: string | null;
};

const PHARMACY_QUOTE_TAG = "pharmacy-quotes";
const PHARMACY_QUOTE_PATIENT_TAG = "patient-provided";
const PHARMACY_QUOTE_PHARMACY_TAG = "quote-pharmacy:";
const PHARMACY_QUOTE_TOTAL_TAG = "quote-total-cents:";
const PHARMACY_QUOTE_NOTE_TAG = "quote-note:";

function encodePharmacyQuoteTag(prefix: string, value: string) {
  return `${prefix}${encodeURIComponent(value)}`;
}

function decodePharmacyQuoteTag(tag: string, prefix: string) {
  if (!tag.startsWith(prefix)) return null;

  try {
    return decodeURIComponent(tag.slice(prefix.length));
  } catch {
    return null;
  }
}

function pharmacyQuoteFromDocument(
  document: PatientDocument,
): PharmacyPatientQuote | null {
  if (
    !document.tags.includes(PHARMACY_QUOTE_TAG) ||
    !document.tags.includes(PHARMACY_QUOTE_PATIENT_TAG)
  ) {
    return null;
  }

  const pharmacyTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_PHARMACY_TAG),
  );
  const totalTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_TOTAL_TAG),
  );
  const noteTag = document.tags.find((tag) =>
    tag.startsWith(PHARMACY_QUOTE_NOTE_TAG),
  );
  const totalCents = totalTag
    ? Number(totalTag.slice(PHARMACY_QUOTE_TOTAL_TAG.length))
    : NaN;
  const upload = document.uploads.find(
    (candidate) => candidate.uploadStatus !== "pending",
  );

  return {
    id: document.id,
    pharmacyName:
      (pharmacyTag
        ? decodePharmacyQuoteTag(pharmacyTag, PHARMACY_QUOTE_PHARMACY_TAG)
        : null) ?? document.name.replace(/^Pharmacy quote – /, ""),
    totalPrice:
      Number.isFinite(totalCents) && totalCents > 0 ? totalCents / 100 : null,
    quoteDate: document.documentDate ?? document.createdAt ?? getTodayString(),
    note: noteTag
      ? (decodePharmacyQuoteTag(noteTag, PHARMACY_QUOTE_NOTE_TAG) ?? "")
      : "",
    source: upload ? "uploaded" : "manual",
    documentName: upload?.name ?? document.name,
    uploadId: upload?.id ?? null,
  };
}

type SelectedPharmacyQuote =
  | { kind: "patient"; quote: PharmacyPatientQuote }
  | {
      kind: "demo";
      quote: PharmacyDemoQuote;
      pharmacy: PharmacyDirectoryRecord;
    };

type PharmacyQuoteFile = {
  source: "camera" | "document";
  name: string;
  uri: string;
  mimeType: string;
  uploadId?: string;
  documentId?: string;
};

type MedicationHelpTopicId =
  | "storage"
  | "injection_disposal"
  | "missed_damaged"
  | "delivery_refills"
  | "travel";

type MedicationHelpTopic = {
  id: MedicationHelpTopicId;
  illustration: number;
  titleKey: string;
  summaryKey: string;
  bodyKey: string;
  nextStepKey: string;
  sourceUrl: string;
};

const pillCapsuleImage =
  require("../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../assets/images/injection-pen.png") as number;
const costEstimateCoinsImage =
  require("../../../assets/images/cost-estimate-coins.png") as number;
const trumpRxCouponEagleImage =
  require("../../../assets/images/trumprx/coupon-eagle.webp") as number;
const trumpRxCouponTextureAsset = Asset.fromModule(
  require("../../../assets/images/trumprx/coupon-texture.svg"),
);
const medicationHelpIllustrations: Record<MedicationHelpTopicId, number> = {
  storage:
    require("../../../assets/images/pharmacy-help/storage.png") as number,
  injection_disposal:
    require("../../../assets/images/pharmacy-help/injection-disposal.png") as number,
  missed_damaged:
    require("../../../assets/images/pharmacy-help/missed-damaged.png") as number,
  delivery_refills:
    require("../../../assets/images/pharmacy-help/delivery-refills.png") as number,
  travel: require("../../../assets/images/pharmacy-help/travel.png") as number,
};
const savingsProgramLogoImages: Record<string, number> = {
  "emd-fertility-instant-savings":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "emd-compassionate-care":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "emd-compassionate-corps":
    require("../../../assets/images/savings-programs/emd-fertility.png") as number,
  "reunite-rx":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "reunite-assist":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "reunite-oncofertility":
    require("../../../assets/images/savings-programs/reunite-rx.png") as number,
  "livestrong-fertility":
    require("../../../assets/images/savings-programs/livestrong.png") as number,
};
const savingsProgramLogoAssets: Record<string, Asset[]> = {
  "inside-rx-fertility": [
    Asset.fromModule(
      require("../../../assets/images/savings-programs/inside-rx.svg"),
    ),
  ],
};
const pharmacyLogoImages: Record<string, number> = {
  "org-pharmacy-freedom":
    require("../../../assets/images/pharmacy-logos/freedom-fertility.png") as number,
  "org-pharmacy-mdr":
    require("../../../assets/images/pharmacy-logos/mdr-specialty-pharmacy.png") as number,
  "org-pharmacy-alto":
    require("../../../assets/images/pharmacy-logos/alto.png") as number,
  "org-pharmacy-vfp":
    require("../../../assets/images/pharmacy-logos/vfp.png") as number,
  "org-pharmacy-apri":
    require("../../../assets/images/pharmacy-logos/apri.png") as number,
  "org-pharmacy-prima":
    require("../../../assets/images/pharmacy-logos/prima.png") as number,
  "org-pharmacy-mandells":
    require("../../../assets/images/pharmacy-logos/mandells.png") as number,
  "org-pharmacy-smp":
    require("../../../assets/images/pharmacy-logos/smp.png") as number,
  "org-pharmacy-cvs-specialty":
    require("../../../assets/images/pharmacy-logos/cvs-specialty.png") as number,
  "org-pharmacy-walgreens-specialty":
    require("../../../assets/images/pharmacy-logos/walgreens.png") as number,
  "org-pharmacy-metro-drugs":
    require("../../../assets/images/pharmacy-logos/metro-drugs.png") as number,
  "org-pharmacy-apthorp":
    require("../../../assets/images/pharmacy-logos/apthorp.png") as number,
  "org-pharmacy-rosemont":
    require("../../../assets/images/pharmacy-logos/rosemont.jpg") as number,
  "org-pharmacy-fuze":
    require("../../../assets/images/pharmacy-logos/fuze-health.png") as number,
};

const BROWN = "#6B5548";
const BROWN_BG = "rgba(139, 111, 94, 0.1)";
const TAG_BORDER = "rgba(107, 85, 72, 0.18)";

const PHARMACY_TAG_LABELS: Record<string, string> = {
  "same-day-delivery": "Same-day delivery",
  "next-day-delivery": "Next-day delivery",
  "overnight-delivery": "Overnight delivery",
  "overnight-shipping": "Overnight shipping",
  "injection-training": "Injection training",
  "cold-chain": "Cold chain",
  "cash-pay": "Cash pay",
  "price-match": "Price match",
  "automatic-discounts": "Automatic discounts",
  "mail-order": "Mail order",
  "auto-refill": "Auto-refill",
  "in-store-pickup": "Pickup",
  "trumprx-participating": "TrumpRx",
  retail: "Retail",
};

const PHARMACY_TAG_ICONS: Record<string, LucideIcon> = {
  "injection-training": Syringe,
  "automatic-discounts": BadgePercent,
  "cash-pay": DollarSign,
  "price-match": Scale,
  "auto-refill": RefreshCw,
  "trumprx-participating": Ticket,
};

const PHARMACY_TAG_STYLE = {
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: 8,
  backgroundColor: "rgba(255, 255, 255, 0.52)",
  borderWidth: 1,
  borderColor: TAG_BORDER,
};

const PHARMACY_TAG_TEXT_STYLE = {
  color: BROWN,
  fontSize: 10,
  fontWeight: "700" as const,
};

const MEDICATION_HELP_TOPICS: MedicationHelpTopic[] = [
  {
    id: "storage",
    illustration: medicationHelpIllustrations.storage,
    titleKey: "access.pharmacy.medication_help_storage_title",
    summaryKey: "access.pharmacy.medication_help_storage_summary",
    bodyKey: "access.pharmacy.medication_help_storage_body",
    nextStepKey: "access.pharmacy.medication_help_storage_next",
    sourceUrl:
      "https://www.fda.gov/drugs/information-consumers-and-patients-drugs/stop-learn-go-tips-talking-your-pharmacist-learn-how-use-medicines-safely",
  },
  {
    id: "injection_disposal",
    illustration: medicationHelpIllustrations.injection_disposal,
    titleKey: "access.pharmacy.medication_help_injection_title",
    summaryKey: "access.pharmacy.medication_help_injection_summary",
    bodyKey: "access.pharmacy.medication_help_injection_body",
    nextStepKey: "access.pharmacy.medication_help_injection_next",
    sourceUrl:
      "https://www.fda.gov/medical-devices/consumer-products/safely-using-sharps-needles-and-syringes-home-work-and-travel",
  },
  {
    id: "missed_damaged",
    illustration: medicationHelpIllustrations.missed_damaged,
    titleKey: "access.pharmacy.medication_help_missed_title",
    summaryKey: "access.pharmacy.medication_help_missed_summary",
    bodyKey: "access.pharmacy.medication_help_missed_body",
    nextStepKey: "access.pharmacy.medication_help_missed_next",
    sourceUrl:
      "https://www.fda.gov/drugs/information-consumers-and-patients-drugs/stop-learn-go-tips-talking-your-pharmacist-learn-how-use-medicines-safely",
  },
  {
    id: "delivery_refills",
    illustration: medicationHelpIllustrations.delivery_refills,
    titleKey: "access.pharmacy.medication_help_delivery_title",
    summaryKey: "access.pharmacy.medication_help_delivery_summary",
    bodyKey: "access.pharmacy.medication_help_delivery_body",
    nextStepKey: "access.pharmacy.medication_help_delivery_next",
    sourceUrl:
      "https://www.healthcare.gov/using-marketplace-coverage/prescription-medications/",
  },
  {
    id: "travel",
    illustration: medicationHelpIllustrations.travel,
    titleKey: "access.pharmacy.medication_help_travel_title",
    summaryKey: "access.pharmacy.medication_help_travel_summary",
    bodyKey: "access.pharmacy.medication_help_travel_body",
    nextStepKey: "access.pharmacy.medication_help_travel_next",
    sourceUrl:
      "https://www.cdc.gov/yellow-book/hcp/travelers-with-additional-considerations/traveling-with-prohibited-or-restricted-medications.html",
  },
];

const DEFAULT_MEDICATION_STORAGE_PROFILE: MedicationStorageProfile = {
  statusKey: "access.pharmacy.medication_help_storage_check_label",
  statusTone: "neutral",
  windowKey: "access.pharmacy.medication_help_storage_unknown_window",
  detailKey: "access.pharmacy.medication_help_storage_unknown_detail",
};

function getMedicationStorageProfile(
  medication: DisplayMedication,
): MedicationStorageProfile {
  const name = medication.name.toLowerCase();

  if (name.includes("gonal-f")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_gonal_window",
      detailKey: "access.pharmacy.medication_help_storage_gonal_detail",
    };
  }

  if (name.includes("cetrotide")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_cetrotide_window",
      detailKey: "access.pharmacy.medication_help_storage_cetrotide_detail",
    };
  }

  if (name.includes("ovidrel")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_refrigerate",
      statusTone: "blue",
      windowKey: "access.pharmacy.medication_help_storage_ovidrel_window",
      detailKey: "access.pharmacy.medication_help_storage_ovidrel_detail",
    };
  }

  if (name.includes("endometrin")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_room_temperature",
      statusTone: "green",
      windowKey: "access.pharmacy.medication_help_storage_endometrin_window",
      detailKey: "access.pharmacy.medication_help_storage_endometrin_detail",
    };
  }

  if (name.includes("dexamethasone")) {
    return {
      statusKey: "access.pharmacy.medication_help_storage_room_temperature",
      statusTone: "green",
      windowKey: "access.pharmacy.medication_help_storage_dexamethasone_window",
      detailKey: "access.pharmacy.medication_help_storage_dexamethasone_detail",
    };
  }

  return DEFAULT_MEDICATION_STORAGE_PROFILE;
}

function getMedicationForm(value?: string | null): MedicationForm {
  const normalized = value?.toLowerCase() ?? "";
  if (normalized.includes("tablet") || normalized.includes("pill")) {
    return "tablet";
  }
  if (normalized.includes("syringe")) return "syringe";
  if (normalized.includes("vial") || normalized.includes("solution")) {
    return "vial";
  }
  if (normalized.includes("suppository")) return "suppository";
  return "pen";
}

function MedFormIcon({
  form,
  size = 32,
}: {
  form: MedicationForm;
  size?: number;
}) {
  const source =
    form === "pen"
      ? injectionPenImage
      : form === "vial" || form === "syringe"
        ? vialSyringeImage
        : pillCapsuleImage;

  return (
    <Image
      source={source}
      style={{ width: size, height: size }}
      resizeMode="contain"
    />
  );
}

function getPharmacyMedicationPrice(
  pharmacy: PharmacyDirectoryRecord,
  medicationId: string,
) {
  return (
    pharmacy.demo?.medicationPrices?.find(
      (entry) => entry.medicationId === medicationId,
    )?.price ?? null
  );
}

function PharmacySavingsProgramCard({
  program,
  onPress,
}: {
  program: PharmacySavingsProgram;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacySavingsProgramCard");
  const coupon = program.coupon;
  const logoImage = savingsProgramLogoImages[program.id];
  const logoAssets = savingsProgramLogoAssets[program.id];

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        width: 204,
        minHeight: 218,
        padding: 14,
        borderRadius: 20,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {coupon ? (
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            opacity: 0.26,
          }}
        >
          <SvgUri
            uri={trumpRxCouponTextureAsset.uri}
            width="100%"
            height="100%"
          />
        </View>
      ) : null}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${program.name}. ${t("access.pharmacy.savings_programs_view_details")}`}
        onPress={() => {
          capturePress(pressLabel(program.name), "savings_program_card");
          onPress();
        }}
        style={{ flex: 1, zIndex: 1 }}
      >
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <View
            style={{
              width: "100%",
              alignItems: "center",
              gap: 5,
            }}
          >
            {coupon ? (
              <Image
                source={trumpRxCouponEagleImage}
                resizeMode="contain"
                style={{ width: 68, height: 60, opacity: 0.9 }}
              />
            ) : logoImage ? (
              <Image
                source={logoImage}
                resizeMode="contain"
                style={{ width: 90, height: 30 }}
              />
            ) : logoAssets ? (
              <View
                style={{
                  height: 30,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                {logoAssets.map((asset, index) => (
                  <SvgUri
                    key={`${program.id}-logo-${index}`}
                    uri={asset.uri}
                    width={index === 0 ? "68" : "62"}
                    height="22"
                  />
                ))}
              </View>
            ) : (
              <HeartHandshake
                size={29}
                color={EMBER.colors.warm}
                strokeWidth={1.5}
                style={{ marginBottom: 3 }}
              />
            )}
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                color: EMBER.colors.text,
                fontSize: 15,
                fontWeight: "800",
                lineHeight: 18,
                textAlign: "center",
              }}
            >
              {program.name}
            </Text>
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                color: EMBER.colors.textLight,
                fontSize: 10.5,
                textAlign: "center",
              }}
            >
              {program.sponsor}
            </Text>
          </View>
          <View
            style={{
              width: "100%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              paddingTop: 12,
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={3}
              style={{
                flexShrink: 1,
                color: EMBER.colors.textLight,
                fontSize: 11,
                lineHeight: 16,
                textAlign: "center",
              }}
            >
              {program.headline}
            </Text>
            <ChevronRight
              size={17}
              color={EMBER.colors.textFaint}
              strokeWidth={1.6}
            />
          </View>
        </View>
      </Pressable>
    </View>
  );
}

function SavingsProgramDialog({
  program,
  onClose,
}: {
  program: PharmacySavingsProgram | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("SavingsProgramDialog");
  if (!program) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={() => {
          // Backdrop dismiss target; it has no visible copy of its own.
          capturePress(pressLabel("dismiss_savings_program"));
          onClose();
        }}
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          backgroundColor: "rgba(20, 12, 9, 0.38)",
        }}
      >
        <Pressable
          onPress={(event) => event.stopPropagation()}
          style={
            {
              width: "100%",
              maxWidth: 440,
              maxHeight: "86%",
              borderRadius: 22,
              overflow: "hidden",
              backgroundColor: "rgba(255,255,255,0.92)",
              borderWidth: 1,
              borderColor: "rgba(255,255,255,0.72)",
              ...(Platform.OS === "web" && {
                backdropFilter: "blur(26px) saturate(1.25)",
                WebkitBackdropFilter: "blur(26px) saturate(1.25)",
              }),
            } as Record<string, unknown>
          }
        >
          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              paddingHorizontal: 20,
              paddingTop: 20,
              paddingBottom: 22,
            }}
          >
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("access.pharmacy.savings_programs_close")),
                  "access.pharmacy.savings_programs_close",
                );
                onClose();
              }}
              accessibilityRole="button"
              accessibilityLabel={t("access.pharmacy.savings_programs_close")}
              hitSlop={10}
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                zIndex: 2,
                width: 32,
                height: 32,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 16,
                backgroundColor: "rgba(107, 85, 72, 0.08)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.textLight, fontSize: 18 }}
              >
                ×
              </Text>
            </Pressable>

            <View style={{ paddingRight: 42 }}>
              <View
                style={{
                  width: 42,
                  height: 42,
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                  borderRadius: 21,
                  backgroundColor: BROWN_BG,
                }}
              >
                <HeartHandshake
                  size={21}
                  color={EMBER.colors.warm}
                  strokeWidth={1.7}
                />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.text,
                  fontSize: 22,
                  fontWeight: "800",
                  lineHeight: 27,
                }}
              >
                {program.name}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textLight,
                  fontSize: 12,
                  lineHeight: 18,
                  marginTop: 4,
                }}
              >
                {program.sponsor}
              </Text>
            </View>

            <View
              style={{
                alignSelf: "flex-start",
                marginTop: 16,
                paddingHorizontal: 9,
                paddingVertical: 5,
                borderRadius: 999,
                backgroundColor: `${EMBER.colors.green}16`,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.green,
                  fontSize: 11,
                  fontWeight: "700",
                }}
              >
                {program.savingsLabel}
              </Text>
            </View>

            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 13,
                lineHeight: 20,
                marginTop: 14,
              }}
            >
              {program.headline}
            </Text>

            <View style={{ gap: 18, marginTop: 22 }}>
              {[
                {
                  title: t("access.pharmacy.savings_programs_eligibility"),
                  body: program.eligibilityLabel,
                },
                {
                  title: t("access.pharmacy.savings_programs_process"),
                  body: program.processLabel,
                },
                {
                  title: t(
                    "access.pharmacy.savings_programs_medications_title",
                  ),
                  body: program.medicationLabel,
                },
              ].map((section) => (
                <View key={section.title}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.text,
                      fontSize: 13,
                      fontWeight: "800",
                      marginBottom: 5,
                    }}
                  >
                    {section.title}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textLight,
                      fontSize: 13,
                      lineHeight: 20,
                    }}
                  >
                    {section.body}
                  </Text>
                </View>
              ))}
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 24,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{ color: EMBER.colors.textFaint, fontSize: 10 }}
              >
                {t("access.pharmacy.savings_programs_verified", {
                  params: { date: program.lastVerifiedOn },
                })}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`${t("access.pharmacy.savings_programs_open")} ${program.name}`}
                onPress={() => {
                  capturePress(
                    pressLabel(t("access.pharmacy.savings_programs_open")),
                    "access.pharmacy.savings_programs_open",
                  );
                  void Linking.openURL(program.sourceUrl);
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  paddingVertical: 5,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.green,
                    fontSize: 11,
                    fontWeight: "700",
                  }}
                >
                  {t("access.pharmacy.savings_programs_open")}
                </Text>
                <ExternalLink
                  size={13}
                  color={EMBER.colors.green}
                  strokeWidth={2}
                />
              </Pressable>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function MedicationHelpCard({
  topic,
  onPress,
}: {
  topic: MedicationHelpTopic;
  onPress: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicationHelpCard");

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(topic.titleKey)}
      onPress={() => {
        capturePress(pressLabel(t(topic.titleKey)), topic.titleKey);
        onPress();
      }}
      style={{
        ...(GLASS_CARD_STYLE as object),
        width: 220,
        minHeight: 146,
        padding: 14,
        borderRadius: 18,
        gap: 12,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Image
          source={topic.illustration}
          resizeMode="contain"
          style={{ width: 72, height: 56, marginLeft: -5 }}
        />
        <ChevronRight
          size={16}
          color={EMBER.colors.textFaint}
          strokeWidth={1.6}
        />
      </View>
      <View style={{ gap: 5 }}>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 14,
            fontWeight: "800",
            lineHeight: 18,
          }}
        >
          {t(topic.titleKey)}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 11,
            lineHeight: 16,
          }}
        >
          {t(topic.summaryKey)}
        </Text>
      </View>
    </Pressable>
  );
}

function MedicationHelpMedicationList({
  topicId,
  medications,
}: {
  topicId: MedicationHelpTopicId;
  medications: DisplayMedication[];
}) {
  const t = useTranslations();
  const sectionKey =
    topicId === "storage"
      ? "access.pharmacy.medication_help_medications_section"
      : topicId === "injection_disposal"
        ? "access.pharmacy.medication_help_injectables_section"
        : topicId === "missed_damaged"
          ? "access.pharmacy.medication_help_missed_section"
          : topicId === "delivery_refills"
            ? "access.pharmacy.medication_help_delivery_section"
            : "access.pharmacy.medication_help_travel_section";

  if (medications.length === 0) {
    return (
      <View style={{ gap: 6 }}>
        <SectionTitle>{t(sectionKey)}</SectionTitle>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 20,
          }}
        >
          {t("access.pharmacy.medication_help_no_medications")}
        </Text>
      </View>
    );
  }

  return (
    <View style={{ gap: 9 }}>
      <SectionTitle>{t(sectionKey)}</SectionTitle>
      {medications.map((medication) => {
        const storage = getMedicationStorageProfile(medication);
        const showStorage = topicId === "storage" || topicId === "travel";
        const rowDetailKey =
          topicId === "injection_disposal"
            ? "access.pharmacy.medication_help_injection_row"
            : topicId === "missed_damaged"
              ? "access.pharmacy.medication_help_missed_row"
              : topicId === "delivery_refills"
                ? "access.pharmacy.medication_help_delivery_row"
                : null;

        return (
          <View
            key={medication.id}
            style={{
              gap: 5,
              padding: 10,
              borderRadius: 13,
              backgroundColor: "rgba(255,255,255,0.38)",
              borderWidth: 1,
              borderColor: "rgba(107,85,72,0.12)",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 9,
              }}
            >
              <MedFormIcon form={medication.form} />
              <View style={{ flex: 1, gap: 5 }}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <View style={{ flex: 1, gap: 1 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 13,
                        fontWeight: "700",
                      }}
                    >
                      {medication.name}
                      {medication.dosage ? ` · ${medication.dosage}` : ""}
                    </Text>
                    {medication.route ? (
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.textFaint,
                          fontSize: 11,
                          lineHeight: 15,
                        }}
                      >
                        {medication.route}
                      </Text>
                    ) : null}
                  </View>
                  {showStorage ? (
                    <Tag
                      label={t(storage.statusKey)}
                      tone={storage.statusTone}
                      variant="soft"
                    />
                  ) : null}
                </View>

                {showStorage ? (
                  <View style={{ gap: 2 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 12,
                        lineHeight: 17,
                      }}
                    >
                      {t(storage.windowKey)}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.textLight,
                        fontSize: 11,
                        lineHeight: 16,
                      }}
                    >
                      {t(storage.detailKey)}
                    </Text>
                  </View>
                ) : rowDetailKey ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textLight,
                      fontSize: 11,
                      lineHeight: 16,
                    }}
                  >
                    {t(rowDetailKey)}
                  </Text>
                ) : null}
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function MedicationHelpDialog({
  topic,
  onClose,
  medications,
}: {
  topic: MedicationHelpTopic | null;
  onClose: () => void;
  medications: DisplayMedication[];
}) {
  const t = useTranslations();
  if (!topic) return null;

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="minimal"
      containerStyle={{
        width: "92%",
        maxWidth: 460,
        maxHeight: "88%",
      }}
      surfaceStyle={{ maxHeight: "100%" }}
      contentStyle={{
        maxHeight: "100%",
        paddingTop: 28,
        paddingHorizontal: 20,
        paddingBottom: 20,
      }}
      testID="medication-help-dialog"
    >
      <ScrollView
        style={{ maxHeight: "100%" }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 16 }}
      >
        <Image
          source={topic.illustration}
          resizeMode="contain"
          style={{ width: 88, height: 72, alignSelf: "center" }}
        />

        <DialogHeader
          style={{ width: "100%" }}
          title={t(topic.titleKey)}
          subtitle={t(topic.summaryKey)}
          titleStyle={{ flex: 1, textAlign: "center" }}
          subtitleStyle={{ width: "100%", textAlign: "center" }}
        />

        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 20,
          }}
        >
          {t(topic.bodyKey)}
        </Text>

        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 20,
          }}
        >
          {t(topic.nextStepKey)}
        </Text>

        <MedicationHelpMedicationList
          topicId={topic.id}
          medications={medications}
        />

        <DialogActions style={{ marginTop: 2 }}>
          <DialogPrimaryButton
            label={t("access.pharmacy.medication_help_open_source")}
            onPress={() => void Linking.openURL(topic.sourceUrl)}
            flex={1}
            trailing={
              <ExternalLink size={14} color="#FFFFFF" strokeWidth={2} />
            }
          />
        </DialogActions>
      </ScrollView>
    </Dialog>
  );
}

function PharmacyInfoCard({
  icon: Icon,
  title,
  body,
  framed = true,
  footer,
  illustration,
}: {
  icon?: LucideIcon;
  title?: string;
  body: string;
  framed?: boolean;
  footer?: ReactNode;
  illustration?: number;
}) {
  return (
    <View
      style={
        framed
          ? { ...(GLASS_CARD_STYLE as object), padding: 18, gap: 8 }
          : { padding: 0, gap: 8 }
      }
    >
      {illustration && !title ? (
        <View style={{ alignItems: "center", gap: 8 }}>
          <Image
            source={illustration}
            resizeMode="contain"
            style={{ width: 86, height: 60 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
              maxWidth: 360,
              textAlign: "center",
            }}
          >
            {body}
          </Text>
        </View>
      ) : (
        <>
          <View
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}
          >
            {illustration ? (
              <Image
                source={illustration}
                resizeMode="contain"
                style={{ width: 64, height: 52, marginTop: -5 }}
              />
            ) : Icon ? (
              <Icon
                size={18}
                color={EMBER.colors.warm}
                strokeWidth={1.6}
                style={{ marginTop: 2 }}
              />
            ) : null}
            {title ? (
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  color: EMBER.colors.text,
                  fontSize: 15,
                  fontWeight: "700",
                }}
              >
                {title}
              </Text>
            ) : null}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
            }}
          >
            {body}
          </Text>
        </>
      )}
      {footer ? <View style={{ marginTop: 4 }}>{footer}</View> : null}
    </View>
  );
}

function PharmacyActionButton({
  icon: Icon,
  label,
  action,
  onPress,
  secondary = false,
}: {
  icon: LucideIcon;
  label: string;
  /** i18n key of `label`, kept stable for analytics across locales. */
  action: string;
  onPress: () => void;
  secondary?: boolean;
}) {
  const capturePress = usePressCapture("PharmacyActionButton");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={() => {
        capturePress(pressLabel(label), action);
        onPress();
      }}
      style={{
        minHeight: 38,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        paddingHorizontal: 13,
        borderRadius: 999,
        borderWidth: secondary ? 1 : 0,
        borderColor: "rgba(107, 85, 72, 0.18)",
        backgroundColor: secondary
          ? "rgba(255, 255, 255, 0.32)"
          : "rgba(107, 85, 72, 0.12)",
      }}
    >
      <Icon size={14} color={BROWN} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        style={{ color: BROWN, fontSize: 12, fontWeight: "800" }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function PharmacyPatientQuoteRow({
  quote,
  divider = false,
  onPress,
}: {
  quote: PharmacyPatientQuote;
  divider?: boolean;
  onPress: () => void;
}) {
  const t = useTranslations();
  const subtitle = [
    t("access.pharmacy.quote_added_date", {
      params: { date: quote.quoteDate },
    }),
    quote.documentName,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Row
      surface="plain"
      title={quote.pharmacyName}
      subtitle={subtitle}
      subtitleVariant="meta"
      leading={
        <FileText size={18} color={EMBER.colors.textLight} strokeWidth={1.7} />
      }
      trailing={
        <Text
          className="font-dm-sans"
          style={{
            color:
              quote.totalPrice != null
                ? EMBER.colors.text
                : EMBER.colors.textLight,
            fontSize: quote.totalPrice != null ? 16 : 11,
            fontWeight: "800",
          }}
        >
          {quote.totalPrice != null
            ? `$${quote.totalPrice.toLocaleString("en-US")}`
            : t("access.pharmacy.quote_price_not_added")}
        </Text>
      }
      chevron
      onPress={onPress}
      action="pharmacy_patient_quote_row"
      accessibilityLabel={t("access.pharmacy.quote_details_a11y", {
        params: { name: quote.pharmacyName },
      })}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: "rgba(107, 85, 72, 0.1)",
      }}
    />
  );
}

function PharmacyDemoQuoteRow({
  pharmacy,
  quote,
  divider = false,
  onPress,
}: {
  pharmacy: PharmacyDirectoryRecord;
  quote: PharmacyDemoQuote;
  divider?: boolean;
  onPress: () => void;
}) {
  const t = useTranslations();
  const logo = pharmacyLogoImages[pharmacy.id];

  return (
    <Row
      surface="plain"
      title={pharmacy.name}
      subtitle={t("access.pharmacy.demo_quote_label")}
      subtitleVariant="meta"
      leading={
        logo ? (
          <Image
            source={logo}
            resizeMode="contain"
            style={{ width: 42, height: 28 }}
          />
        ) : (
          <Store size={18} color={EMBER.colors.textLight} strokeWidth={1.7} />
        )
      }
      trailing={
        <View style={{ alignItems: "flex-end", gap: 3 }}>
          {quote.price != null ? (
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.text,
                fontSize: 16,
                fontWeight: "800",
              }}
            >
              {`$${quote.price.toLocaleString("en-US")}`}
            </Text>
          ) : (
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 11,
                fontWeight: "700",
              }}
            >
              {t("access.pharmacy.contact_for_pricing")}
            </Text>
          )}
          {quote.estimatedSavings ? (
            <Tag
              label={t("access.pharmacy.demo_quote_savings", {
                params: {
                  amount: quote.estimatedSavings.toLocaleString("en-US"),
                },
              })}
              tone="green"
              variant="soft"
              style={PHARMACY_TAG_STYLE}
              textStyle={PHARMACY_TAG_TEXT_STYLE}
            />
          ) : null}
        </View>
      }
      chevron
      onPress={onPress}
      action="pharmacy_demo_quote_row"
      accessibilityLabel={t("access.pharmacy.quote_details_a11y", {
        params: { name: pharmacy.name },
      })}
      style={{
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: "rgba(107, 85, 72, 0.1)",
      }}
    />
  );
}

function PharmacyQuoteDetailDialog({
  selection,
  onClose,
}: {
  selection: SelectedPharmacyQuote | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const getDownloadUrl = useGetDownloadUrl();
  if (!selection) return null;

  const isPatientQuote = selection.kind === "patient";
  const patientUploadId = isPatientQuote ? selection.quote.uploadId : null;

  const openAttachment = async () => {
    if (!patientUploadId) return;
    try {
      const url = await getDownloadUrl.mutateAsync({
        uploadId: patientUploadId,
        disposition: "inline",
      });
      if (typeof url === "string") await Linking.openURL(url);
    } catch (error) {
      console.error("Failed to open pharmacy quote attachment:", error);
    }
  };
  const title = isPatientQuote
    ? selection.quote.pharmacyName
    : selection.pharmacy.name;
  const subtitle = isPatientQuote
    ? t("access.pharmacy.quote_detail_patient_subtitle", {
        params: { date: selection.quote.quoteDate },
      })
    : t("access.pharmacy.quote_detail_demo_subtitle");
  const total = isPatientQuote
    ? selection.quote.totalPrice
    : selection.quote.price;

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="minimal"
      containerStyle={{ width: "92%", maxWidth: 440 }}
      contentStyle={{ paddingTop: 28, paddingHorizontal: 20 }}
      testID="pharmacy-quote-detail-dialog"
    >
      <DialogHeader title={title} subtitle={subtitle} />
      <View
        style={{
          marginTop: 18,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: "rgba(107, 85, 72, 0.1)",
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          overflow: "hidden",
        }}
      >
        <Row
          surface="plain"
          title={t("access.pharmacy.quote_detail_total")}
          trailing={
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.text,
                fontSize: 16,
                fontWeight: "800",
              }}
            >
              {total != null
                ? `$${total.toLocaleString("en-US")}`
                : t("access.pharmacy.quote_price_not_added")}
            </Text>
          }
          chevron={false}
          paddingHorizontal={12}
          paddingVertical={11}
        />
        {!isPatientQuote && selection.quote.estimatedSavings ? (
          <Row
            surface="plain"
            title={t("access.pharmacy.quote_detail_estimated_savings")}
            trailing={
              <Tag
                label={t("access.pharmacy.demo_quote_savings", {
                  params: {
                    amount:
                      selection.quote.estimatedSavings.toLocaleString("en-US"),
                  },
                })}
                tone="green"
                variant="soft"
                style={PHARMACY_TAG_STYLE}
                textStyle={PHARMACY_TAG_TEXT_STYLE}
              />
            }
            chevron={false}
            paddingHorizontal={12}
            paddingVertical={11}
            style={{
              borderTopWidth: 1,
              borderTopColor: "rgba(107, 85, 72, 0.1)",
            }}
          />
        ) : null}
        <Row
          surface="plain"
          title={t("access.pharmacy.quote_detail_source")}
          trailing={
            <Text
              className="font-dm-sans"
              style={{ color: EMBER.colors.textLight, fontSize: 12 }}
            >
              {isPatientQuote
                ? selection.quote.source === "uploaded"
                  ? t("access.pharmacy.quote_detail_uploaded")
                  : t("access.pharmacy.quote_detail_manual")
                : t("access.pharmacy.quote_detail_demo")}
            </Text>
          }
          chevron={false}
          paddingHorizontal={12}
          paddingVertical={11}
          style={{
            borderTopWidth: 1,
            borderTopColor: "rgba(107, 85, 72, 0.1)",
          }}
        />
        {isPatientQuote && selection.quote.documentName ? (
          <Row
            surface="plain"
            title={t("access.pharmacy.quote_detail_document")}
            trailing={
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  maxWidth: 170,
                  color: patientUploadId
                    ? EMBER.colors.text
                    : EMBER.colors.textLight,
                  fontSize: 12,
                  fontWeight: patientUploadId ? "700" : "400",
                }}
              >
                {selection.quote.documentName}
              </Text>
            }
            chevron={!!patientUploadId}
            onPress={patientUploadId ? openAttachment : undefined}
            paddingHorizontal={12}
            paddingVertical={11}
            style={{
              borderTopWidth: 1,
              borderTopColor: "rgba(107, 85, 72, 0.1)",
            }}
          />
        ) : null}
      </View>
      {isPatientQuote && selection.quote.note ? (
        <View style={{ gap: 5, marginTop: 16 }}>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.text,
              fontSize: 12,
              fontWeight: "800",
            }}
          >
            {t("access.pharmacy.quote_detail_note")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 13,
              lineHeight: 19,
            }}
          >
            {selection.quote.note}
          </Text>
        </View>
      ) : null}
      {!isPatientQuote ? (
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 16,
            color: EMBER.colors.textLight,
            fontSize: 13,
            lineHeight: 19,
          }}
        >
          {t("access.pharmacy.demo_detail_quote_body")}
        </Text>
      ) : null}
      <DialogActions style={{ marginTop: 18 }}>
        <DialogSecondaryButton
          label={t("access.pharmacy.quote_detail_close")}
          onPress={onClose}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}

function PharmacyQuoteCaptureModal({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacyQuoteCaptureModal");
  const [pharmacyName, setPharmacyName] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [quoteDate, setQuoteDate] = useState(getTodayString());
  const [note, setNote] = useState("");
  const [quoteFile, setQuoteFile] = useState<PharmacyQuoteFile | null>(null);
  const [busySource, setBusySource] = useState<
    "camera" | "document" | "save" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const createDocument = useCreateDocument();
  const getUploadUrl = useGetUploadUrl();
  const uploadFile = useUploadFile();
  const completeUpload = useCompleteUpload();

  const resetDraft = () => {
    setPharmacyName("");
    setTotalPrice("");
    setQuoteDate(getTodayString());
    setNote("");
    setQuoteFile(null);
    setBusySource(null);
    setError(null);
  };

  const close = () => {
    resetDraft();
    onClose();
  };

  const selectFile = async (source: PharmacyQuoteFile["source"]) => {
    setBusySource(source);
    setError(null);
    try {
      if (source === "camera") {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) return;
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: ["images"],
          quality: 0.82,
        });
        if (!result.canceled && result.assets?.[0]) {
          const asset = result.assets[0];
          setQuoteFile({
            source,
            name: `pharmacy-quote-${Date.now()}.jpg`,
            uri: asset.uri,
            mimeType: resolveUploadContentType({
              name: "pharmacy-quote.jpg",
              uri: asset.uri,
              mimeType: asset.mimeType,
            }),
          });
        }
      } else {
        const result = await DocumentPicker.getDocumentAsync({
          type: ["application/pdf", "image/*"],
          copyToCacheDirectory: true,
        });
        if (!result.canceled && result.assets?.[0]) {
          const asset = result.assets[0];
          setQuoteFile({
            source,
            name: asset.name,
            uri: asset.uri,
            mimeType: resolveUploadContentType({
              name: asset.name,
              uri: asset.uri,
              mimeType: asset.mimeType,
            }),
          });
        }
      }
    } catch (selectionError) {
      console.error("Failed to select pharmacy quote:", selectionError);
      setError(t("access.pharmacy.quote_upload_error"));
    } finally {
      setBusySource(null);
    }
  };

  const save = async () => {
    const parsedPrice = Number(totalPrice.replace(/[^0-9.]/g, ""));
    const hasPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;
    if (!pharmacyName.trim() || (!hasPrice && !quoteFile)) return;

    setBusySource("save");
    setError(null);
    try {
      let uploadId: string | null = quoteFile?.uploadId ?? null;
      if (quoteFile && !uploadId) {
        const upload = await getUploadUrl.mutateAsync({
          contentType: quoteFile.mimeType,
          name: quoteFile.name,
        });
        const fileData = await readFileForUpload({
          uri: quoteFile.uri,
          name: quoteFile.name,
          mimeType: quoteFile.mimeType,
        });
        await uploadFile.mutateAsync({
          uploadUrl: upload.uploadUrl,
          file: fileData,
          contentType: quoteFile.mimeType,
        });
        await completeUpload.mutateAsync(upload.uploadId);
        uploadId = upload.uploadId;
        setQuoteFile((current) =>
          current ? { ...current, uploadId: upload.uploadId } : current,
        );
      }
      await createDocument.mutateAsync({
        name: `Pharmacy quote – ${pharmacyName.trim()}`,
        documentDate: quoteDate.trim() || getTodayString(),
        tags: [
          PHARMACY_QUOTE_TAG,
          PHARMACY_QUOTE_PATIENT_TAG,
          encodePharmacyQuoteTag(
            PHARMACY_QUOTE_PHARMACY_TAG,
            pharmacyName.trim(),
          ),
          ...(hasPrice
            ? [`${PHARMACY_QUOTE_TOTAL_TAG}${Math.round(parsedPrice * 100)}`]
            : []),
          ...(note.trim()
            ? [encodePharmacyQuoteTag(PHARMACY_QUOTE_NOTE_TAG, note.trim())]
            : []),
        ],
        targetSlots: ["costs"],
        sourceType: uploadId ? "uploaded_file" : "unclassified",
        sharedWith: ["patient"],
        uploads: uploadId ? [{ id: uploadId }] : [],
      });
      close();
    } catch (saveError) {
      console.error("Failed to save pharmacy quote:", saveError);
      setError(t("access.pharmacy.quote_upload_error"));
      setBusySource(null);
    }
  };

  const parsedPrice = Number(totalPrice.replace(/[^0-9.]/g, ""));
  const canSave =
    pharmacyName.trim().length > 0 &&
    ((Number.isFinite(parsedPrice) && parsedPrice > 0) || quoteFile != null);

  const inputStyle = {
    minHeight: 42,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: "rgba(42, 37, 34, 0.09)",
    backgroundColor: "rgba(255, 255, 255, 0.36)",
    paddingHorizontal: 12,
    color: EMBER.colors.text,
    fontSize: 13,
    fontFamily: "DMSans_400Regular",
  } as const;

  const renderInput = (
    label: string,
    value: string,
    onChangeText: (value: string) => void,
    placeholder: string,
    options: {
      keyboardType?: "default" | "decimal-pad";
      multiline?: boolean;
    } = {},
  ) => (
    <View style={{ gap: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.textLight,
          fontSize: 11,
          fontWeight: "800",
        }}
      >
        {label}
      </Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={EMBER.colors.textFaint}
        keyboardType={options.keyboardType ?? "default"}
        multiline={options.multiline}
        style={
          options.multiline
            ? {
                ...inputStyle,
                minHeight: 72,
                paddingVertical: 10,
                textAlignVertical: "top",
              }
            : inputStyle
        }
      />
    </View>
  );

  const renderSource = (
    source: PharmacyQuoteFile["source"],
    label: string,
    Icon: LucideIcon,
  ) => (
    <Pressable
      accessibilityRole="button"
      onPress={() => {
        capturePress(pressLabel(label), `quote_source_${source}`);
        void selectFile(source);
      }}
      disabled={busySource != null}
      style={{
        flex: 1,
        minHeight: 76,
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: "rgba(42, 37, 34, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.3)",
        opacity: busySource && busySource !== source ? 0.5 : 1,
      }}
    >
      <Icon size={21} color={BROWN} strokeWidth={1.7} />
      <Text
        className="font-dm-sans"
        style={{ color: EMBER.colors.text, fontSize: 12, fontWeight: "700" }}
      >
        {busySource === source ? t("access.pharmacy.quote_opening") : label}
      </Text>
    </Pressable>
  );

  if (!visible) return null;

  return (
    <Dialog
      visible={visible}
      onClose={close}
      closeButton="with-background"
      containerStyle={{ width: "92%", maxWidth: 460 }}
      surfaceStyle={{ maxHeight: "92%" }}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 20,
        paddingBottom: 20,
      }}
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 15 }}
      >
        <DialogHeader
          title={t("access.pharmacy.quote_add_title")}
          subtitle={t("access.pharmacy.quote_add_body")}
        />

        {renderInput(
          t("access.pharmacy.quote_pharmacy_label"),
          pharmacyName,
          setPharmacyName,
          t("access.pharmacy.quote_pharmacy_placeholder"),
        )}
        <View style={{ flexDirection: "row", gap: 9 }}>
          <View style={{ flex: 1 }}>
            {renderInput(
              t("access.pharmacy.quote_total_label"),
              totalPrice,
              setTotalPrice,
              t("access.pharmacy.quote_total_placeholder"),
              { keyboardType: "decimal-pad" },
            )}
          </View>
          <View style={{ flex: 1 }}>
            {renderInput(
              t("access.pharmacy.quote_date_label"),
              quoteDate,
              setQuoteDate,
              t("access.pharmacy.quote_date_placeholder"),
            )}
          </View>
        </View>
        {renderInput(
          t("access.pharmacy.quote_notes_label"),
          note,
          setNote,
          t("access.pharmacy.quote_notes_placeholder"),
          { multiline: true },
        )}

        <View style={{ gap: 8 }}>
          <Text
            className="font-dm-sans"
            style={{
              color: EMBER.colors.textLight,
              fontSize: 11,
              fontWeight: "800",
            }}
          >
            {t("access.pharmacy.quote_attach_title")}
          </Text>
          <View style={{ flexDirection: "row", gap: 9 }}>
            {renderSource(
              "camera",
              t("access.pharmacy.quote_take_photo"),
              Camera,
            )}
            {renderSource(
              "document",
              t("access.pharmacy.quote_upload_file"),
              FileText,
            )}
          </View>
          {quoteFile ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                paddingHorizontal: 10,
                paddingVertical: 8,
                borderRadius: 12,
                backgroundColor: "rgba(107, 85, 72, 0.08)",
              }}
            >
              <Check size={14} color={BROWN} strokeWidth={2} />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  flex: 1,
                  color: BROWN,
                  fontSize: 11,
                  fontWeight: "700",
                }}
              >
                {quoteFile.name}
              </Text>
            </View>
          ) : null}
        </View>

        {error ? (
          <Text
            accessibilityRole="alert"
            className="font-dm-sans"
            style={{ color: EMBER.colors.coral, fontSize: 12, lineHeight: 17 }}
          >
            {error}
          </Text>
        ) : null}

        <DialogActions>
          <DialogSecondaryButton
            label={t("access.pharmacy.quote_cancel")}
            onPress={close}
            flex={1}
          />
          <DialogPrimaryButton
            label={t("access.pharmacy.quote_save")}
            onPress={() => void save()}
            disabled={!canSave}
            loading={busySource === "save"}
            flex={1}
            hapticStyle={null}
          />
        </DialogActions>
      </ScrollView>
    </Dialog>
  );
}

function PharmacyLogo({ pharmacy }: { pharmacy: PharmacyDirectoryRecord }) {
  const logo = pharmacyLogoImages[pharmacy.id];

  if (logo) {
    return (
      <Image
        source={logo}
        resizeMode="contain"
        style={{ width: 116, height: 46 }}
      />
    );
  }

  return (
    <View
      style={{
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 13,
        backgroundColor: BROWN_BG,
      }}
    >
      <Building2 size={20} color={BROWN} strokeWidth={1.7} />
    </View>
  );
}

function PharmacyCard({
  pharmacy,
  onPress,
  coverageLabel,
  cashPayLabel,
  contactForPricing,
  demoMode,
  detailsA11yLabel,
  cashPriceLabel,
  callLabel,
}: {
  pharmacy: PharmacyDirectoryRecord;
  onPress: () => void;
  coverageLabel: string;
  cashPayLabel: string;
  contactForPricing: string;
  demoMode: boolean;
  detailsA11yLabel: string;
  cashPriceLabel: string;
  callLabel: string;
}) {
  const capturePress = usePressCapture("PharmacyCard");
  const callButton = pharmacy.phone ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${callLabel} ${pharmacy.name} ${pharmacy.phone}`}
      onPress={(event) => {
        capturePress(pressLabel(pharmacy.phone), "call_pharmacy");
        event.stopPropagation();
        void Linking.openURL(`tel:${pharmacy.phone?.replace(/[^\d+]/g, "")}`);
      }}
      style={{
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        paddingHorizontal: 8,
        paddingVertical: 5,
        borderRadius: 999,
        backgroundColor: BROWN_BG,
      }}
    >
      <Phone size={11} color={BROWN} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        style={{ color: BROWN, fontSize: 10, fontWeight: "600" }}
      >
        {pharmacy.phone}
      </Text>
    </Pressable>
  ) : null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={detailsA11yLabel}
      onPress={() => {
        capturePress(pressLabel(pharmacy.name), "pharmacy_card");
        onPress();
      }}
      style={
        {
          flex: 1,
          width: "100%",
          minWidth: 0,
          gap: 8,
          padding: 16,
          borderRadius: 22,
          overflow: "hidden",
          backgroundColor: "rgba(255, 255, 255, 0.4)",
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.6)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.04,
          shadowRadius: 16,
          minHeight: 282,
          ...(Platform.OS === "web" && {
            backdropFilter: "blur(40px)",
            WebkitBackdropFilter: "blur(40px)",
          }),
        } as Record<string, unknown>
      }
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 46,
        }}
      >
        <PharmacyLogo pharmacy={pharmacy} />
        <ChevronRight
          accessibilityElementsHidden
          importantForAccessibility="no"
          size={18}
          color={EMBER.colors.warm}
          strokeWidth={1.8}
        />
      </View>

      <Text
        className="font-dm-sans"
        numberOfLines={2}
        style={{
          color: EMBER.colors.text,
          fontSize: 15,
          fontWeight: "700",
          lineHeight: 20,
        }}
      >
        {pharmacy.name}
      </Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        {pharmacy.specialties
          .filter(
            (specialty) =>
              ![
                "fertility",
                "cold-chain",
                "retail",
                "specialty",
                "mail_order",
                "same-day-delivery",
                "next-day-delivery",
                "overnight-delivery",
                "overnight-shipping",
                "in-store-pickup",
              ].includes(specialty),
          )
          .slice(0, 2)
          .map((specialty) => (
            <Tag
              key={specialty}
              label={PHARMACY_TAG_LABELS[specialty] ?? specialty}
              icon={PHARMACY_TAG_ICONS[specialty]}
              variant="outline"
              tone="neutral"
              style={PHARMACY_TAG_STYLE}
              textStyle={PHARMACY_TAG_TEXT_STYLE}
            />
          ))}
      </View>

      {demoMode && pharmacy.demo ? (
        <>
          <Tag
            label={pharmacy.demo.delivery}
            icon={Truck}
            variant="outline"
            tone="green"
            style={{
              ...PHARMACY_TAG_STYLE,
              alignSelf: "flex-start",
              marginTop: 2,
              backgroundColor: "rgba(130, 212, 168, 0.18)",
              borderColor: "rgba(78, 127, 97, 0.2)",
            }}
            textStyle={{ ...PHARMACY_TAG_TEXT_STYLE, color: "#4E7F61" }}
          />
          <View style={{ marginTop: "auto", paddingTop: 10, gap: 10 }}>
            <View>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 9,
                  fontWeight: "700",
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                }}
              >
                {pharmacy.demo.totalPrice == null
                  ? contactForPricing
                  : cashPriceLabel}
              </Text>
              {pharmacy.demo.totalPrice != null ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    color: EMBER.colors.text,
                    fontSize: 18,
                    fontWeight: "800",
                    lineHeight: 23,
                  }}
                >
                  ${pharmacy.demo.totalPrice.toLocaleString("en-US")}
                </Text>
              ) : null}
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 10,
                  marginTop: 2,
                }}
              >
                {pharmacy.demo.acceptsInsurance == null
                  ? coverageLabel
                  : pharmacy.demo.acceptsInsurance
                    ? coverageLabel
                    : cashPayLabel}
              </Text>
            </View>
            {callButton}
          </View>
        </>
      ) : (
        <View style={{ marginTop: "auto", paddingTop: 10, gap: 10 }}>
          <Text
            className="font-dm-sans"
            style={{ color: EMBER.colors.textLight, fontSize: 11 }}
          >
            {coverageLabel}
          </Text>
          {callButton}
        </View>
      )}
    </Pressable>
  );
}

export default function PharmacyScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PharmacyScreen");
  const [demoMode, setDemoMode] = useState(() => {
    if (route.params?.demo === true) return true;
    if (Platform.OS !== "web" || typeof window === "undefined") return false;
    const demoParam = new URLSearchParams(window.location.search).get("demo");
    return demoParam === "1" || demoParam === "true";
  });
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const rootNavigation =
    navigation.getParent<BottomTabNavigationProp<MainTabParamList>>();
  const {
    data: patientMeds,
    isLoading: medicationsLoading,
    isError: medicationsError,
  } = usePatientMedications();
  const {
    data: pharmacies,
    isLoading: pharmaciesLoading,
    error: pharmaciesError,
  } = usePharmacyDirectory({ demo: demoMode });
  const { data: documents = [] } = useDocuments();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<PharmacyTab>("prescription");
  const [typeFilter, setTypeFilter] = useState<PharmacyDirectoryType | null>(
    null,
  );
  const [selectedFactsheet, setSelectedFactsheet] =
    useState<ContentBlock<"medication"> | null>(null);
  const [selectedUserContext, setSelectedUserContext] = useState<
    MedicationUserContext | undefined
  >(undefined);
  const [selectedSavingsProgram, setSelectedSavingsProgram] =
    useState<PharmacySavingsProgram | null>(null);
  const [selectedQuote, setSelectedQuote] =
    useState<SelectedPharmacyQuote | null>(null);
  const [selectedMedicationHelp, setSelectedMedicationHelp] =
    useState<MedicationHelpTopic | null>(null);
  const [quoteModalVisible, setQuoteModalVisible] = useState(false);

  const patientQuotes = useMemo(() => {
    if (demoMode) return [];

    return documents
      .map(pharmacyQuoteFromDocument)
      .filter((quote): quote is PharmacyPatientQuote => quote !== null)
      .sort((a, b) => b.quoteDate.localeCompare(a.quoteDate));
  }, [demoMode, documents]);

  const openMedicationFlow = useCallback(() => {
    rootNavigation?.navigate("Care", {
      screen: "CareOverview",
      params: { focus: "medication" },
    });
  }, [rootNavigation]);

  const displayMeds = useMemo<DisplayMedication[]>(() => {
    if (demoMode) return MOCK_PHARMACY_MEDICATIONS;

    return (patientMeds ?? [])
      .filter(
        (medication) =>
          medication.medication?.name || medication.rawMedicationName,
      )
      .map((medication) => ({
        id: medication.medicationId ?? medication.id,
        name: medication.medication?.name ?? medication.rawMedicationName ?? "",
        dosage: medication.dose
          ? `${medication.dose} ${medication.doseUnit ?? ""}`.trim()
          : "",
        form: getMedicationForm(medication.medication?.intakeType),
      }));
  }, [demoMode, patientMeds]);

  const bestMedicationPrices = useMemo(() => {
    const result: Record<
      string,
      { price: number; pharmacy: PharmacyDirectoryRecord }
    > = {};
    if (!demoMode) return result;

    displayMeds.forEach((medication) => {
      const best = pharmacies
        .map((pharmacy) => ({
          pharmacy,
          price: getPharmacyMedicationPrice(pharmacy, medication.id),
        }))
        .filter(
          (
            candidate,
          ): candidate is {
            pharmacy: PharmacyDirectoryRecord;
            price: number;
          } => candidate.price != null,
        )
        .sort((a, b) => a.price - b.price)[0];

      if (best) result[medication.id] = best;
    });

    return result;
  }, [demoMode, displayMeds, pharmacies]);

  const handleMedPress = useCallback((medication: DisplayMedication) => {
    const factsheet = getMedicationByBrandName(medication.name) ?? null;
    if (!factsheet) return;
    setSelectedFactsheet(factsheet);
    setSelectedUserContext({
      dosage: medication.dosage,
      route: medication.route ?? null,
    });
  }, []);

  const handleFulfillmentMedicationPress = useCallback(
    (payload: HomepageMedicationFactSheetPayload) => {
      setSelectedFactsheet(payload.item);
      setSelectedUserContext(payload.userContext);
    },
    [],
  );

  const filteredPharmacies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pharmacies.filter((pharmacy) => {
      const matchesSearch =
        !query ||
        pharmacy.name.toLowerCase().includes(query) ||
        pharmacy.city?.toLowerCase().includes(query) ||
        pharmacy.state?.toLowerCase().includes(query);
      const matchesType = !typeFilter || pharmacy.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [pharmacies, search, typeFilter]);

  const rows = useMemo(() => {
    const result: PharmacyDirectoryRecord[][] = [];
    for (let index = 0; index < filteredPharmacies.length; index += 2) {
      result.push(filteredPharmacies.slice(index, index + 2));
    }
    return result;
  }, [filteredPharmacies]);

  const quoteRows = useMemo(() => {
    const cards = MOCK_PHARMACY_QUOTES.reduce<
      { quote: PharmacyDemoQuote; pharmacy: PharmacyDirectoryRecord }[]
    >((result, quote) => {
      const pharmacy = pharmacies.find(
        (candidate) => candidate.id === quote.pharmacyId,
      );
      if (pharmacy) result.push({ quote, pharmacy });
      return result;
    }, []);
    const result: (typeof cards)[] = [];
    for (let index = 0; index < cards.length; index += 2) {
      result.push(cards.slice(index, index + 2));
    }
    return result;
  }, [pharmacies]);

  const savingsProgramCount = PHARMACY_SAVINGS_PROGRAMS.length;

  const filterOptions: {
    key: PharmacyDirectoryType | null;
    label: string;
    icon: LucideIcon;
  }[] = [
    { key: null, label: t("access.pharmacy.filter_all"), icon: Layers },
    {
      key: "specialty",
      label: t("access.pharmacy.filter_specialty"),
      icon: Building2,
    },
    {
      key: "mail_order",
      label: t("access.pharmacy.filter_mail_order"),
      icon: Mail,
    },
    { key: "retail", label: t("access.pharmacy.filter_retail"), icon: Store },
  ];

  const quoteActions = (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 8,
      }}
    >
      <PharmacyActionButton
        icon={UploadCloud}
        label={t("access.pharmacy.quote_upload")}
        action="access.pharmacy.quote_upload"
        onPress={() => setQuoteModalVisible(true)}
      />
      <PharmacyActionButton
        icon={DollarSign}
        label={t("access.pharmacy.quote_add_price")}
        action="access.pharmacy.quote_add_price"
        onPress={() => setQuoteModalVisible(true)}
        secondary
      />
    </View>
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          width: "100%",
          maxWidth: 520,
          alignSelf: "center",
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            route.params?.returnTab ? t("you.shared.back") : t("access.back")
          }
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("access.back"),
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ color: EMBER.colors.warm, fontSize: 14 }}
          >
            {route.params?.returnTab ? t("you.shared.back") : t("access.back")}
          </Text>
        </Pressable>

        <PharmacyHeader
          rightAccessory={
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Sparkles size={16} color={EMBER.colors.warm} strokeWidth={2} />
              <MoonToggle
                value={demoMode}
                onChange={(next) => {
                  setDemoMode(next);
                  setSearch("");
                  setTypeFilter(null);
                }}
                testID="pharmacy-demo-toggle"
                accessibilityLabel={t("access.pharmacy.demo_mode_title")}
                accessibilityHint={t("access.pharmacy.demo_mode_hint")}
              />
            </View>
          }
        />

        <CostsCardTabs
          active={activeTab}
          onChange={setActiveTab}
          options={[
            {
              value: "prescription",
              label: t("access.pharmacy.tab_prescription"),
              icon: Pill,
            },
            {
              value: "quotes",
              label: t("access.pharmacy.tab_quotes"),
              icon: DollarSign,
            },
            {
              value: "savings",
              label: t("access.pharmacy.tab_savings"),
              icon: HeartHandshake,
            },
          ]}
          style={{ marginTop: 24 }}
        />

        {activeTab === "prescription" ? (
          <>
            <View
              style={{
                ...(GLASS_CARD_STYLE as object),
                marginTop: 0,
                padding: 0,
                paddingBottom: displayMeds.length === 0 ? 0 : 12,
                overflow: "hidden",
              }}
            >
              {displayMeds.length > 0 ||
              (!demoMode && (medicationsLoading || medicationsError)) ? (
                <View
                  style={{
                    paddingHorizontal: 18,
                    paddingTop: 18,
                    paddingBottom: 12,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.text,
                      fontSize: 17,
                      fontWeight: "700",
                    }}
                  >
                    {t("access.pharmacy.section_meds_title")}
                  </Text>
                </View>
              ) : null}

              {!demoMode && medicationsLoading ? (
                <View style={{ paddingHorizontal: 18, paddingBottom: 18 }}>
                  <PharmacyInfoCard
                    icon={Shield}
                    title={t("access.pharmacy.meds_loading_title")}
                    body={t("access.pharmacy.meds_loading_body")}
                    framed={false}
                  />
                </View>
              ) : !demoMode && medicationsError ? (
                <View style={{ paddingHorizontal: 18, paddingBottom: 18 }}>
                  <PharmacyInfoCard
                    icon={Shield}
                    title={t("access.pharmacy.meds_error_title")}
                    body={t("access.pharmacy.meds_error_body")}
                    framed={false}
                  />
                </View>
              ) : displayMeds.length === 0 ? (
                <View
                  style={{
                    paddingHorizontal: 18,
                    paddingTop: 16,
                    paddingBottom: 6,
                  }}
                >
                  <PharmacyInfoCard
                    body={t("access.pharmacy.meds_empty_body")}
                    framed={false}
                    illustration={pillCapsuleImage}
                    footer={
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <PharmacyActionButton
                          icon={Pill}
                          label={t("access.pharmacy.meds_add_medication")}
                          action="access.pharmacy.meds_add_medication"
                          onPress={openMedicationFlow}
                        />
                        <PharmacyActionButton
                          icon={UploadCloud}
                          label={t("access.pharmacy.meds_upload_prescription")}
                          action="access.pharmacy.meds_upload_prescription"
                          onPress={openMedicationFlow}
                          secondary
                        />
                      </View>
                    }
                  />
                </View>
              ) : (
                displayMeds.map((medication) => (
                  <Pressable
                    key={medication.id}
                    accessibilityRole="button"
                    accessibilityLabel={t(
                      "access.pharmacy.medication_guide_a11y",
                      {
                        params: { name: medication.name },
                      },
                    )}
                    onPress={() => {
                      capturePress(
                        pressLabel(medication.name),
                        "medication_row",
                      );
                      handleMedPress(medication);
                    }}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 10,
                    }}
                  >
                    <MedFormIcon form={medication.form} />
                    <View style={{ flex: 1 }}>
                      <Text
                        className="font-dm-sans"
                        style={{
                          color: EMBER.colors.text,
                          fontSize: 14,
                          fontWeight: "500",
                        }}
                      >
                        {medication.name}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        {medication.dosage ? (
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.textLight,
                              fontSize: 12,
                            }}
                          >
                            {medication.dosage}
                          </Text>
                        ) : null}
                        {demoMode && medication.route ? (
                          <Text
                            className="font-dm-sans"
                            style={{
                              color: EMBER.colors.textFaint,
                              fontSize: 11,
                            }}
                          >
                            · {medication.route}
                          </Text>
                        ) : null}
                        {demoMode && medication.coverage ? (
                          <View
                            style={{
                              paddingHorizontal: 7,
                              paddingVertical: 2,
                              borderRadius: 999,
                              backgroundColor:
                                medication.coverage === "covered"
                                  ? `${EMBER.colors.green}18`
                                  : BROWN_BG,
                            }}
                          >
                            <Text
                              className="font-dm-sans"
                              style={{
                                color:
                                  medication.coverage === "covered"
                                    ? EMBER.colors.green
                                    : BROWN,
                                fontSize: 9,
                                fontWeight: "700",
                              }}
                            >
                              {t(
                                medication.coverage === "covered"
                                  ? "access.pharmacy.coverage.covered"
                                  : "access.pharmacy.coverage.cash_pay",
                              )}
                            </Text>
                          </View>
                        ) : null}
                      </View>
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      {(() => {
                        const bestPrice = bestMedicationPrices[medication.id];
                        return (
                          <>
                            {bestPrice &&
                            pharmacyLogoImages[bestPrice.pharmacy.id] ? (
                              <Image
                                source={
                                  pharmacyLogoImages[bestPrice.pharmacy.id]
                                }
                                resizeMode="contain"
                                style={{ width: 44, height: 17 }}
                              />
                            ) : null}
                            {bestPrice ? (
                              <Text
                                className="font-dm-sans"
                                style={{
                                  color: EMBER.colors.text,
                                  fontSize: 12,
                                  fontWeight: "700",
                                }}
                              >
                                {`$${bestPrice.price.toLocaleString("en-US")}`}
                              </Text>
                            ) : null}
                            <ChevronRight
                              size={16}
                              color={EMBER.colors.textFaint}
                              strokeWidth={1.5}
                            />
                          </>
                        );
                      })()}
                    </View>
                  </Pressable>
                ))
              )}
            </View>

            {demoMode ? (
              <View style={{ marginTop: 28 }}>
                <View style={{ marginBottom: 12 }}>
                  <SectionTitle icon={Truck}>
                    {t("access.pharmacy.fulfillment_section_eyebrow")}
                  </SectionTitle>
                </View>
                <View style={{ gap: 10 }}>
                  {pharmacyFulfillmentCards.map((card) =>
                    card.kind === "refill-action" ||
                    card.kind === "pickup-action" ? (
                      <PharmacyActionCard
                        key={card.id}
                        card={card}
                        onOpenMedicationFactSheet={
                          handleFulfillmentMedicationPress
                        }
                      />
                    ) : (
                      <DesignedPharmacyFulfillmentCard
                        key={card.id}
                        card={card}
                        onOpenMedicationFactSheet={
                          handleFulfillmentMedicationPress
                        }
                      />
                    ),
                  )}
                </View>
              </View>
            ) : null}

            <View style={{ marginTop: 28 }}>
              <View style={{ marginBottom: 12 }}>
                <SectionTitle icon={Shield}>
                  {t("access.pharmacy.medication_help_section_eyebrow")}
                </SectionTitle>
              </View>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 10, paddingRight: 16 }}
                style={{ marginHorizontal: -16, paddingLeft: 16 }}
              >
                {MEDICATION_HELP_TOPICS.map((topic) => (
                  <MedicationHelpCard
                    key={topic.id}
                    topic={topic}
                    onPress={() => setSelectedMedicationHelp(topic)}
                  />
                ))}
              </ScrollView>
            </View>
          </>
        ) : null}

        {activeTab === "quotes" ? (
          <View
            style={{
              ...(GLASS_CARD_STYLE as object),
              padding: 14,
              gap: 10,
              overflow: "hidden",
            }}
          >
            <PharmacyInfoCard
              body={t("access.pharmacy.quote_empty_body")}
              footer={
                <View style={{ alignItems: "center" }}>
                  {demoMode || patientQuotes.length > 0 ? (
                    quoteActions
                  ) : (
                    <PharmacyActionButton
                      icon={UploadCloud}
                      label={t("access.pharmacy.quote_upload_or_add_price")}
                      action="access.pharmacy.quote_upload_or_add_price"
                      onPress={() => setQuoteModalVisible(true)}
                    />
                  )}
                </View>
              }
              illustration={costEstimateCoinsImage}
              framed={false}
            />
            {patientQuotes.length > 0 ? (
              <View
                style={{
                  borderRadius: 16,
                  borderWidth: 1,
                  borderColor: "rgba(107, 85, 72, 0.1)",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  overflow: "hidden",
                }}
              >
                {patientQuotes.map((quote, index) => (
                  <PharmacyPatientQuoteRow
                    key={quote.id}
                    quote={quote}
                    divider={index > 0}
                    onPress={() => setSelectedQuote({ kind: "patient", quote })}
                  />
                ))}
              </View>
            ) : null}
            {demoMode ? (
              <View
                style={{
                  borderRadius: 16,
                  borderWidth: 1,
                  borderColor: "rgba(107, 85, 72, 0.1)",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  overflow: "hidden",
                }}
              >
                {quoteRows
                  .flatMap((row) => row)
                  .map(({ quote, pharmacy }, index) => (
                    <PharmacyDemoQuoteRow
                      key={pharmacy.id}
                      pharmacy={pharmacy}
                      quote={quote}
                      divider={index > 0}
                      onPress={() =>
                        setSelectedQuote({ kind: "demo", quote, pharmacy })
                      }
                    />
                  ))}
              </View>
            ) : null}
          </View>
        ) : null}

        {activeTab === "savings" ? (
          <View
            style={{
              ...(GLASS_CARD_STYLE as object),
              paddingTop: 16,
              paddingLeft: 16,
              paddingBottom: 16,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                paddingRight: 16,
                paddingBottom: 14,
                gap: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.text,
                  fontSize: 16,
                  fontWeight: "700",
                }}
              >
                {t("access.pharmacy.savings_summary_title", {
                  params: { count: String(savingsProgramCount) },
                })}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textLight,
                  fontSize: 12,
                  lineHeight: 18,
                }}
              >
                {t("access.pharmacy.savings_summary_body")}
              </Text>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 10, paddingRight: 16 }}
            >
              {PHARMACY_SAVINGS_PROGRAMS.map((program) => (
                <PharmacySavingsProgramCard
                  key={program.id}
                  program={program}
                  onPress={() => setSelectedSavingsProgram(program)}
                />
              ))}
            </ScrollView>
          </View>
        ) : null}

        <View style={{ marginTop: 28 }}>
          <View style={{ marginBottom: 12 }}>
            <SectionTitle icon={Store}>
              {t("access.pharmacy.section_directory_eyebrow")}
            </SectionTitle>
          </View>

          <MoonSearchInput
            placeholder={t("access.pharmacy.search_placeholder")}
            value={search}
            onChangeText={setSearch}
            onClear={() => setSearch("")}
            tone="glass"
            style={{ marginBottom: 14 }}
          />

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingBottom: 14 }}
          >
            {filterOptions.map((option) => (
              <MoonFilterChip
                key={option.label}
                label={option.label}
                icon={option.icon}
                selected={typeFilter === option.key}
                onPress={() => setTypeFilter(option.key)}
                tone="glass"
                showCheck={false}
              />
            ))}
          </ScrollView>

          {pharmaciesLoading ? (
            <PharmacyInfoCard
              icon={Database}
              title={t("access.pharmacy.directory_loading_title")}
              body={t("access.pharmacy.directory_loading_body")}
            />
          ) : pharmaciesError && pharmacies.length === 0 ? (
            <PharmacyInfoCard
              icon={Database}
              title={t("access.pharmacy.directory_error_title")}
              body={t("access.pharmacy.directory_error_body")}
            />
          ) : (
            <>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textFaint,
                  fontSize: 13,
                  marginBottom: 14,
                }}
              >
                {t(
                  filteredPharmacies.length === 1
                    ? "access.pharmacy.result_count_one"
                    : "access.pharmacy.result_count_other",
                  { params: { count: String(filteredPharmacies.length) } },
                )}
              </Text>
              <View style={{ gap: 12 }}>
                {rows.map((row, rowIndex) => (
                  <View
                    key={rowIndex}
                    style={{
                      width: "100%",
                      flexDirection: "row",
                      gap: 12,
                    }}
                  >
                    {row.map((pharmacy) => (
                      <View key={pharmacy.id} style={{ flex: 1, minWidth: 0 }}>
                        <PharmacyCard
                          pharmacy={pharmacy}
                          coverageLabel={t("access.pharmacy.coverage_varies")}
                          cashPayLabel={t("access.pharmacy.cash_pay_only")}
                          contactForPricing={t(
                            "access.pharmacy.contact_for_pricing",
                          )}
                          demoMode={demoMode}
                          detailsA11yLabel={t(
                            "access.pharmacy.view_details_a11y",
                            { params: { name: pharmacy.name } },
                          )}
                          cashPriceLabel={t("access.pharmacy.cash_price_label")}
                          callLabel={t("access.pharmacy.call_pharmacy")}
                          onPress={() =>
                            navigation.navigate("PharmacyDetail", {
                              pharmacyId: pharmacy.id,
                              demo: demoMode,
                            })
                          }
                        />
                      </View>
                    ))}
                    {row.length === 1 ? (
                      <View style={{ flex: 1, minWidth: 0 }} />
                    ) : null}
                  </View>
                ))}
              </View>
              {filteredPharmacies.length === 0 ? (
                <View style={{ alignItems: "center", paddingTop: 40 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.textLight,
                      fontSize: 15,
                      textAlign: "center",
                    }}
                  >
                    {t("access.pharmacy.empty")}
                  </Text>
                </View>
              ) : null}
            </>
          )}
        </View>
      </ScrollView>

      <MedicationFactsheetSheet
        item={selectedFactsheet}
        onClose={() => {
          setSelectedFactsheet(null);
          setSelectedUserContext(undefined);
        }}
        userContext={selectedUserContext}
      />

      <SavingsProgramDialog
        program={selectedSavingsProgram}
        onClose={() => setSelectedSavingsProgram(null)}
      />
      <MedicationHelpDialog
        topic={selectedMedicationHelp}
        onClose={() => setSelectedMedicationHelp(null)}
        medications={displayMeds}
      />
      <PharmacyQuoteCaptureModal
        visible={quoteModalVisible}
        onClose={() => setQuoteModalVisible(false)}
      />
      <PharmacyQuoteDetailDialog
        selection={selectedQuote}
        onClose={() => setSelectedQuote(null)}
      />
    </GlassScreenLayout>
  );
}
